import {Box, Button, Typography} from "@mui/material";
import React from "react";

export type FileAttachment = {
    filename: string;
    content: string; // base64
}

interface AttachmentsProps {
    attachments: FileAttachment[];
    onAttachmentsChange: (attachments: FileAttachment[]) => void;
    onError: (message: string) => void;
}

export default function AttachmentsComponent({attachments, onAttachmentsChange, onError}: AttachmentsProps) {

    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!files) return;

        const newAttachments: FileAttachment[] = [];
        const filesToProcess = Math.min(files.length, 5 - attachments.length); // Ensure max 5 total

        for (let i = 0; i < filesToProcess; i++) {
            const file = files[i];

            // Check file size (5MB limit)
            if (file.size > 5 * 1024 * 1024) {
                onError(`Plik "${file.name}" przekracza limit 5MB`);
                continue;
            }

            const reader = new FileReader();
            reader.onload = (e) => {
                const base64Content = e.target?.result as string;
                // Remove data:mime/type;base64, prefix
                const base64Data = base64Content.split(',')[1];

                newAttachments.push({
                    filename: file.name,
                    content: base64Data
                });

                // Update state when all files are processed
                if (newAttachments.length === filesToProcess) {
                    onAttachmentsChange([...attachments, ...newAttachments]);
                }
            };
            reader.readAsDataURL(file);
        }

        // Clear the input to allow selecting the same file again
        event.target.value = '';
    };

    const removeAttachment = (index: number) => {
        onAttachmentsChange(attachments.filter((_, i) => i !== index));
    };

    return (
        <Box sx={{ mt: 2, mb: 2 }}>
            <Typography variant="body2" gutterBottom>
                Załączniki (maks. 5 plików, każdy do 5MB).
                UWAGA, załączniki zostaną przesłane jedynie do odbiorców z aktywną skrzynką odbiorczą.
            </Typography>

            <Button
                variant="outlined"
                component="label"
                disabled={attachments.length >= 5}
                sx={{ mb: 2 }}
            >
                Dodaj plik
                <input
                    type="file"
                    hidden
                    multiple
                    onChange={handleFileUpload}
                    accept="*/*"
                />
            </Button>

            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
            }}>
            </Box>

            {attachments.map((attachment, index) => (
                <Box key={index} sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 1,
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    mb: 1
                }}>
                    <Typography variant="body2">
                        {attachment.filename}
                    </Typography>
                    <Button
                        size="small"
                        color="error"
                        onClick={() => removeAttachment(index)}
                    >
                        Usuń
                    </Button>
                </Box>
            ))}

            <Typography variant="caption" color="text.secondary">
                Załączono: {attachments.length}/5 plików
            </Typography>
        </Box>
    );
}
